<template>
  <template v-if="state.status === 'pending'">
    <SectionContainer
      bg-color="light"
      class="product-page__header"
      height="none"
    >
      <ProductPageHeaderSkeleton />
    </SectionContainer>
    <SectionContainer>
      <SHeadingSkeleton size="2" width="15rem" />
      <STextSkeleton :lines="2" />
      <ContentBenefitsSkeleton />
    </SectionContainer>
    <SectionContainer>
      <ContentTextWithImageSkeleton />
    </SectionContainer>
    <SectionContainer>
      <SHeadingSkeleton size="2" width="12.5rem" />
      <STextSkeleton :lines="5" />
    </SectionContainer>
    <SectionContainer>
      <SHeadingSkeleton size="2" width="11.25rem" />
      <PAccordionSkeleton :amount="3" />
    </SectionContainer>
    <SectionContainer bg-color="primary">
      <SButtonListSkeleton />
    </SectionContainer>
    <SectionContainer
      bg-color="primary"
      class="product-page__cta-form"
      height="none"
    >
      <SHeadingSkeleton size="3" width="12.5rem" />
      <ContentCtaFormSkeleton />
    </SectionContainer>
    <SectionContainer>
      <SHeadingSkeleton size="2" width="15rem" />
      <STextSkeleton width="100%" />
      <SReviewsSkeleton :amount="3" />
    </SectionContainer>
  </template>

  <section v-else-if="state.status === 'error'">
    <SErrorMessage retry size="lg" @retry="refetch" />
  </section>

  <div v-else-if="state.status === 'success'" class="product-page">
    <SectionRenderer
      v-for="section in state.data.sections"
      :key="section.id"
      :author="state.data.updatedBy?.name ?? null"
      :fallback-form-url="fallbackFormUrl"
      :geo-product-slug="state.data.geoProductCategory.slug"
      :has-local-search="state.data.geoProductCategory.hasLocalSearch"
      :jumplinks="state.data.jumplinks"
      :last-update="state.data.lastModified"
      :section="section"
    />
  </div>
</template>

<script setup lang="ts">
import { useQuery } from "@pinia/colada";
import { computed } from "vue";

import {
  PAccordionSkeleton,
  SButtonListSkeleton,
  SErrorMessage,
  SHeadingSkeleton,
  SReviewsSkeleton,
  STextSkeleton,
} from "@solvari/components";

import { useProductPage } from "~/features/product/api/cms.api.ts";
import ProductPageHeaderSkeleton from "~/features/product/components/ProductPageHeaderSkeleton.vue";
import ContentBenefitsSkeleton from "~/shared/components/content-blocks/ContentBenefitsSkeleton.vue";
import ContentCtaFormSkeleton from "~/shared/components/content-blocks/ContentCtaFormSkeleton.vue";
import ContentTextWithImageSkeleton from "~/shared/components/content-blocks/ContentTextWithImageSkeleton.vue";
import SectionRenderer from "~/shared/components/content-blocks/SectionRenderer.vue";
import SectionContainer from "~/shared/components/SectionContainer.vue";
import { keepPageWarm } from "~/shared/composables/useCacheTags.ts";
import { useCmsSeo } from "~/shared/composables/useCmsSeo.ts";
import { useFocusPageLayout } from "~/shared/composables/useFocusPageLayout.ts";
import { useHostLocale } from "~/shared/composables/useHost.ts";
import { useWebPageStructuredData } from "~/shared/composables/useMetaData.ts";
import { usePageContext } from "~/shared/composables/usePageContext.ts";
import { useProvideContentParams } from "~/shared/composables/useProvideContentParams.ts";
import { useRequiredParams } from "~/shared/libs/routeParams.ts";
import { ziggy } from "~/shared/libs/ziggy.ts";

const locale = useHostLocale();
const { slug } = useRequiredParams("slug");

usePageContext({ pageType: "product" });

const { state, refetch } = useQuery(
  useProductPage({ locale, uri: `/${slug}` }),
);

useFocusPageLayout(state);

keepPageWarm();

const fallbackFormUrl = ziggy("mainSite.geoProduct.leadForm", {
  geoProductSlug: slug,
  formSlug: undefined,
});

useProvideContentParams({
  slug,
  locale,
  localSearch: computed(() => state.value.data?.localSearch),
});

useCmsSeo(() => state.value.data?.seo, {
  ogType: "article",
  articlePublishedTime: () => state.value.data?.date,
  variantSourceUrl: () => state.value.data?.variantSource?.url,
});

useWebPageStructuredData(() => state.value.data?.seo);
</script>

<style lang="postcss">
.product-page .s-app-container {
  @apply flex flex-col gap-8;

  > .content-cta-form {
    @apply mx-auto w-xl max-w-full;
  }
}
</style>
